import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMolecule } from '@peruselab/api-client/molecules';
import { createBiopolymer } from '@peruselab/api-client/biopolymers';
import {
  buildBiopolymerResidues,
  type BiopolymerResiduePick
} from '@peruselab/structure-editor/chemistry/biopolymerResidue';

type SaveState =
  | { readonly state: 'idle' }
  | { readonly state: 'saving' }
  | { readonly state: 'success'; readonly message: string }
  | { readonly state: 'error'; readonly message: string };

let nextLocalId = 0;
function createLocalId(): string {
  nextLocalId += 1;
  return `biopolymer-residue-${nextLocalId}`;
}

// #150: the client-side counterpart to #149's biopolymerDrawing schema, mirroring
// useIllustrationComposer's shape. Simpler than Reaction/Illustration in one respect: a residue
// needs no depiction/build step at save time (#133's decision record: peptide-only v1 carries no
// depiction concept), so moleculeRevision is captured once when a residue is added rather than
// re-fetched at save time.
export function useBiopolymerComposer() {
  const { t } = useTranslation();
  const [residues, setResidues] = useState<readonly BiopolymerResiduePick[]>([]);
  const [saveState, setSaveState] = useState<SaveState>({ state: 'idle' });

  // Deliberately does not dedupe by moleculeId: the same amino acid can legitimately appear more
  // than once in a real sequence (e.g. two glycines), each its own residue at its own position.
  const addResidue = useCallback(async (moleculeId: string) => {
    const molecule = await getMolecule(moleculeId);
    setResidues((current) => [
      ...current,
      { localId: createLocalId(), moleculeId, moleculeRevision: molecule.documentRevision }
    ]);
  }, []);

  const removeResidue = useCallback((localId: string) => {
    setResidues((current) => current.filter((residue) => residue.localId !== localId));
  }, []);

  const moveResidue = useCallback((localId: string, direction: 'up' | 'down') => {
    setResidues((current) => {
      const index = current.findIndex((residue) => residue.localId === localId);
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (index === -1 || targetIndex < 0 || targetIndex >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(index, 1);
      next.splice(targetIndex, 0, moved!);
      return next;
    });
  }, []);

  const canSave = residues.length > 0 && saveState.state !== 'saving';

  const save = useCallback(
    async (title?: string) => {
      if (residues.length === 0) {
        setSaveState({ state: 'error', message: 'A biopolymer needs at least one residue.' });
        return;
      }
      setSaveState({ state: 'saving' });
      try {
        const created = await createBiopolymer(
          {
            schemaVersion: 'chem-drawing.v11',
            kind: 'biopolymer',
            residues: buildBiopolymerResidues(residues)
          },
          title
        );
        setSaveState({
          state: 'success',
          message: t('biopolymerComposer.created', {
            title: created.title || title || t('biopolymerComposer.untitled')
          })
        });
      } catch (error) {
        setSaveState({
          state: 'error',
          message: error instanceof Error ? error.message : 'Unable to save this biopolymer.'
        });
      }
    },
    [residues, t]
  );

  return { residues, addResidue, removeResidue, moveResidue, saveState, canSave, save };
}
